import { useState, useEffect } from 'react';
import { AlertTriangle, Trash2, Loader2, X } from 'lucide-react';
import { useApp } from '../lib/AppContext';
import { useEffectiveUser } from '../hooks/useEffectiveUser';
import { supabase } from '../lib/supabaseClient';
import Header from '../components/Header';
import Button from '../components/Button';
import SuccessModal from '../components/SuccessModal';

export default function DeleteAccount() {
  const { navigateTo, showToast } = useApp();
  const { user, isLoggedOut, isPreviewMode } = useEffectiveUser();
  const [isDeleting, setIsDeleting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showCancelSuccessModal, setShowCancelSuccessModal] = useState(false);

  useEffect(() => {
    if (isLoggedOut || !user) {
      navigateTo('profile-settings');
    }
  }, [isLoggedOut, user, navigateTo]);

  const handleDeleteClick = () => {
    setShowConfirmModal(true);
  };

  const handleCancelDelete = () => {
    setShowConfirmModal(false);
    setShowCancelSuccessModal(true);
  };

  const handleCancelSuccessClose = () => {
    setShowCancelSuccessModal(false);
    navigateTo('profile-settings');
  };

  const handleConfirmDelete = async () => {
    if (!user) {
      showToast('No user found');
      return;
    }

    if (isPreviewMode) {
      showToast('Preview mode: Account deletion disabled');
      setShowConfirmModal(false);
      return;
    }

    setIsDeleting(true);

    try {
      // Delete user account using Supabase Auth
      // Note: This uses the standard user deletion method
      // The user must be authenticated to delete their own account
      const { error } = await supabase.rpc('delete_user');

      // If RPC function doesn't exist, we'll use the auth API directly
      if (error && error.message.includes('function')) {
        // Try alternative: sign out (account deletion requires admin API or custom setup)
        // For production, you'd need to implement a server-side function
        console.warn('User deletion RPC not available, signing out instead');

        await supabase.auth.signOut();
        showToast('Account deletion requires admin setup. You have been signed out.');
        setShowConfirmModal(false);
        navigateTo('auth');
        return;
      }

      if (error) {
        console.error('Error deleting account:', error);
        showToast('Failed to delete account. Please try again.');
        setIsDeleting(false);
        return;
      }

      // Success - clear session and navigate to onboarding
      await supabase.auth.signOut();
      showToast('Your account has been deleted');
      setShowConfirmModal(false);
      navigateTo('home');

    } catch (error) {
      console.error('Unexpected error during account deletion:', error);
      showToast('An unexpected error occurred. Please try again.');
      setIsDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#111111]" style={{ paddingBottom: 'calc(92px + env(safe-area-inset-bottom, 0px) + 16px)' }}>
      <Header
        mode="title"
        title="Delete Account"
        showBackButton
        onBackClick={() => navigateTo('profile-settings')}
      />

      <div className="px-6 py-6">
        <div className="max-w-[600px] mx-auto">
          <div className="bg-[#181818] rounded-xl border border-[#282828] pt-6 px-6 pb-3" style={{ boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)' }}>
            {/* Warning Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#FF6F61]/10 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-[#FF6F61]" />
              </div>
              <h2 className="text-[17px] font-semibold text-[#FF6F61]">
                Permanently delete account
              </h2>
            </div>

            {/* Warning Text */}
            <div className="mb-6">
              <p className="text-[14px] text-[#C0C0C0] leading-relaxed">
                Deleting your account removes your profile, library, and all personal data. This action cannot be undone.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="space-y-2">
              <Button
                onClick={handleDeleteClick}
                variant="destructive"
                fullWidth
              >
                <Trash2 className="w-4 h-4" />
                Delete My Account
              </Button>

              <Button
                onClick={() => navigateTo('profile-settings')}
                variant="tertiary"
                fullWidth
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200"
          style={{ paddingTop: 'env(safe-area-inset-top)' }}
        >
          <div className="bg-[#181818] border border-[#282828] rounded-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-300" style={{ boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)' }}>
            <Button
              onClick={handleCancelDelete}
              disabled={isDeleting}
              variant="tertiary"
              className="absolute top-4 right-4 !p-2 !min-h-0 !h-auto z-10"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-white/60" />
            </Button>

            <div className="px-6 pt-8 pb-4 text-center">
              <div className="mb-4 flex justify-center">
                <div className="w-16 h-16 bg-[#FF6F61]/20 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-8 h-8 text-[#FF6F61]/95" />
                </div>
              </div>

              <h2 className="text-2xl font-bold text-white mb-3">
                Are you sure?
              </h2>

              <p className="text-white/80 text-[15px] leading-relaxed">
                This will permanently delete your account and all associated data.
              </p>
            </div>

            <div className="px-6 pb-3 space-y-2">
              <Button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                variant="destructive"
                fullWidth
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete Account
                  </>
                )}
              </Button>

              <Button
                onClick={handleCancelDelete}
                disabled={isDeleting}
                variant="tertiary"
                fullWidth
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Success Modal */}
      <SuccessModal
        isOpen={showCancelSuccessModal}
        onDismiss={handleCancelSuccessClose}
        title="Account preserved"
        message="Your account is still active. You can continue exploring civicbeat anytime."
      />
    </div>
  );
}
